import type { TCurrencyData } from "components/AdminPanel/types";
import ModalBox from "components/Modal";
import CurrencyAutocomplete from "components/shared/CurrencyAutocomplete";
import CurrencyIcon from "components/shared/CurrencyIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import WorkSelectField from "components/TitleFormContainer/WorkSelectField";
import { getCurrenciesOptions, getCurrencyData, preventSubmit, priceTransform } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store/store";
import { fetchCreateNewTitle } from "store/titleStore";
import { invalidateWorks } from "store/workStore";
import { v4 as uuid } from "uuid";

import styles from "./style.module.scss";

type TFormNewTitleSimplified = {
    name: "";
    ean13: string;
    price: string;
    currency: TCurrencyData;
    work: { id: number; name: string } | null;
};

/**
 * This component render simplified title form in a modal
 * EAN13 and Price fields aren't mandatory
 * We put a "draft_" + generated UUID to the ean13 value if the field is empty.
 */
interface INewTitleSimplifiedModal {
    open: boolean;
    onClose: () => void;
}

const NewTitleSimplifiedModal = ({ open, onClose }: INewTitleSimplifiedModal) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { getCompanySettings, companyInfos } = useCompanySettings();
    const dispatch = useAppDispatch();
    const usesPriceWithVat = getCompanySettings("uses_price_with_vat");
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const activeAlternativeCurrencies = JSON.parse(getCompanySettings("active_alternative_currencies"));
    const [isLoading, setIsLoading] = useState(false);

    const defaultCurrency = getCurrencyData(companyInfos?.currency ?? "");

    const currenciesOptions = useMemo(
        () => getCurrenciesOptions(activeAlternativeCurrencies),
        [activeAlternativeCurrencies],
    );

    const defaultFormValues: TFormNewTitleSimplified = {
        name: "",
        ean13: "",
        price: "",
        currency: getCurrencyData(companyInfos?.currency ?? "EUR"),
        work: null,
    };

    const methods = useForm({
        defaultValues: defaultFormValues,
        mode: "all",
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = methods;

    const onSubmit: SubmitHandler<TFormNewTitleSimplified> = async (form) => {
        setIsLoading(true);
        const { work, ...formWithoutWork } = form;
        const req = {
            ...formWithoutWork,
            ean13: form.ean13 || `draft_${uuid()}`,
            price: priceTransform({
                price: form.price,
                usesVAT: usesPriceWithVat,
                countryCode: companyInfos?.countryCode,
            }),
            currency: form?.currency?.code,
            releaseDate: null,
            otherInformation: "",
            workId: work?.id,
        };
        const response = await dispatch(fetchCreateNewTitle({ data: req as any }));
        if (response.meta.requestStatus === "fulfilled") {
            dispatch(invalidateWorks());
            const createdTitleId = response.payload.result.result.createdTitleId;
            const createdWorkId = response.payload.result.result.createdWorkId ?? work?.id;
            navigate(createdWorkId ? `/works/${createdWorkId}` : `/titles/${createdTitleId}`);
            onClose();
        }
        setIsLoading(false);
    };

    useEffect(() => {
        reset(defaultFormValues);
    }, [open]);

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={t("NewTitle.NewTitle")}
            subTitle={t("NewTitle.CreateProjectTitle")}
        >
            <form
                className={styles["new-title-modal-simplified"]}
                onSubmit={handleSubmit(onSubmit)}
                onKeyDown={preventSubmit}
            >
                <div className={styles["new-title-modal-simplified-fields"]}>
                    <MessageBlock type="info" small text={t("NewTitle.WorkOptionalHint")} />
                    <WorkSelectField
                        control={control}
                        error={errors.work}
                        required={false}
                        allowCreate
                        suggestedName={watch("name")}
                        onSelectWork={(work) => setValue("work", work)}
                    />
                    <TextFormComponent
                        label={t("NewTitle.Title")}
                        control={control}
                        controlName="name"
                        required
                        error={errors.name}
                    />

                    {usesMultipleCurrencies ? (
                        // Fields layout with multiple currencies
                        <div className={styles["use-muti-currencies-layout"]}>
                            <TextFormComponent
                                label={t("NewTitle.ISBN")}
                                control={control}
                                controlName="ean13"
                                fullWidth
                                showOptionalMark
                            />
                            <div className={styles["splitted-fields"]}>
                                <NumberFormComponent
                                    label={usesPriceWithVat ? t("NewTitle.PriceVAT") : t("NewTitle.Price")}
                                    control={control}
                                    controlName="price"
                                    showOptionalMark
                                    fullWidth
                                />
                                <div style={{ width: "50%" }}>
                                    <CurrencyAutocomplete
                                        control={control}
                                        controlName="currency"
                                        currenciesList={[defaultCurrency, ...currenciesOptions]}
                                        vatUsed={usesPriceWithVat}
                                        companyCurrency={companyInfos?.currency ?? "EUR"}
                                        errors={errors}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Fields layout without multiple currencies
                        <div className={styles["splitted-fields"]}>
                            <TextFormComponent
                                label={t("NewTitle.ISBN")}
                                control={control}
                                controlName="ean13"
                                fullWidth
                                showOptionalMark
                            />
                            <div style={{ width: "50%" }}>
                                <NumberFormComponent
                                    label={usesPriceWithVat ? t("NewTitle.PriceVAT") : t("NewTitle.Price")}
                                    control={control}
                                    controlName="price"
                                    showOptionalMark
                                    icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <StyledButton buttonType="primary" type="submit" fullWidth loading={isLoading}>
                    {t("NewTitle.CreateTitle")}
                </StyledButton>
            </form>
        </ModalBox>
    );
};

export default NewTitleSimplifiedModal;
